import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { DocumentChartBarIcon, EyeIcon } from "@heroicons/react/24/outline";
import React, { Fragment } from "react";

import { IconButton, LoadingSpinner } from "..";
import { MovementTypeOption, Player, ReportType, Session } from "../../types";

type AnalysisCellProps = {
  element: any;
  fetchingReports: boolean;
  session: Session | undefined;
  openReportsModal: (player: any, session: any) => void;
  showVisualizeButton?: boolean;
  handleVisualizeButton?: (data: Session | Player) => void;
  visualizeButtonData?: Session | Player;
  movementTypeOptions?: MovementTypeOption[];
};

export const AnalysisCell: React.FC<AnalysisCellProps> = ({
  element,
  fetchingReports,
  session,
  openReportsModal,
  showVisualizeButton,
  handleVisualizeButton,
  visualizeButtonData,
  movementTypeOptions,
}: AnalysisCellProps): JSX.Element => {
  const urls = element.row.original.reports
    ? element.row.original.reports.map((report: ReportType) => report.reportUrl)
    : [];

  const hasMultipleMovementTypes =
    movementTypeOptions && movementTypeOptions.length > 1;

  return (
    <div data-testid="analysis-cell" className="flex items-center gap-2">
      {showVisualizeButton && urls.length > 0 && (
        <>
          {hasMultipleMovementTypes ? (
            <Menu as="div" className="relative">
              <MenuButton
                as="button"
                className="inline-block mr-2 py-2 px-2.5 border border-[#D1D5DB] rounded-md shadow-sm text-sm font-bold text-[#FB6A07] hover:bg-[#F5A623]/[0.06] active:bg-[#FF7A1F]/[0.12] active:text-[#FB6A07] disabled:hover:bg-white disabled:cursor-not-allowed disabled:text-[#CACCCF]"
                data-testid="visualize-button-dropdown"
              >
                <EyeIcon className="h-5 w-5 stroke-2" />
              </MenuButton>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <MenuItems className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                  <div className="py-1">
                    {movementTypeOptions?.map((option) => (
                      <MenuItem key={option.slug}>
                        <button
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 data-[active]:bg-gray-100 data-[active]:text-gray-900"
                          onClick={() =>
                            option.handler(visualizeButtonData || session!)
                          }
                          data-testid={`visualize-${option.slug}-button`}
                        >
                          Visualize {option.label}
                        </button>
                      </MenuItem>
                    ))}
                  </div>
                </MenuItems>
              </Transition>
            </Menu>
          ) : (
            <IconButton
              data-testid="visualize-button"
              onClick={() =>
                handleVisualizeButton?.(visualizeButtonData || session!)
              }
              className="mr-2"
            >
              <EyeIcon className="h-5 w-5 stroke-2" />
            </IconButton>
          )}
        </>
      )}
      {fetchingReports && !urls.length ? (
        <div data-testid="upload-spinner-container">
          <LoadingSpinner />
        </div>
      ) : urls.length ? (
        urls.length > 1 ? (
          <IconButton
            data-testid="analysis-button"
            onClick={() => openReportsModal(element.row.original, session)}
          >
            <span className="flex">
              <DocumentChartBarIcon className="w-5 mr-2 stroke-2" /> (
              {urls.length})
            </span>
          </IconButton>
        ) : (
          <a
            href={urls[0]}
            target="_blank"
            rel="noreferrer"
            data-testid="single-analysis-link"
          >
            <IconButton data-testid="analysis-button">
              <DocumentChartBarIcon className="w-5 stroke-2" />
            </IconButton>
          </a>
        )
      ) : (
        <IconButton disabled data-testid="disabled-analysis-button">
          <DocumentChartBarIcon className="w-5 stroke-2" />
        </IconButton>
      )}
    </div>
  );
};
